/* File: frontend/src/components/reputation/TruthAttackDashboard.js */
import React, { useState, useEffect } from 'react';
import apiService from '../../services/apiService';
import {
    LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine
} from 'recharts';
import { Shield, Swords, TrendingUp, AlertCircle, CheckCircle2, ArrowRight } from 'lucide-react';

// --- STYLES & CONFIG ---
const COLORS = {
    google: '#4285F4',
    gpt4: '#10A37F',
    gemini: '#4E86F8', // Blue-ish for Gemini
    deepseek: '#6366F1', // Indigo
    grok: '#000000',
    grid: '#e2e8f0',
    text: '#64748b'
};

const TabButton = ({ active, onClick, icon: Icon, label }) => (
    <button
        onClick={onClick}
        style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '10px 16px',
            background: active ? '#fff' : 'transparent',
            border: 'none',
            borderBottom: active ? '2px solid #2563eb' : '2px solid transparent',
            color: active ? '#1e293b' : '#64748b',
            fontWeight: active ? 600 : 500,
            cursor: 'pointer',
            transition: 'all 0.2s'
        }}
    >
        <Icon size={16} />
        {label}
    </button>
);

// Helper function to format rank display
const formatRankDisplay = (rank) => {
    if (rank === null || rank === undefined) return "—";
    if (rank >= 999) return "Not in Top 10";
    return `#${rank}`;
};

const TruthAttackDashboard = ({ profileId }) => {
    const [activeTab, setActiveTab] = useState('audit'); // 'audit', 'attack', 'impact'
    const [data, setData] = useState({ audit: [], attack: [], impact: [] });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!profileId) return;
        fetchAnalytics();
    }, [profileId]);

    const fetchAnalytics = async () => {
        setLoading(true);
        try {
            const [audit, attack, impact] = await Promise.all([
                apiService.makeRequest(`/reputation/analytics/audit?profile_id=${profileId}`).catch(() => []),
                apiService.makeRequest(`/reputation/analytics/attack?profile_id=${profileId}`).catch(() => []),
                apiService.makeRequest(`/reputation/analytics/comparison?profile_id=${profileId}`).catch(() => [])
            ]);

            setData({ audit, attack, impact });
        } catch (error) {
            console.error("Failed to fetch analytics:", error);
        } finally {
            setLoading(false);
        }
    };

    // --- TRANSFORMERS ---
    const transformSeriesData = (seriesList) => {
        if (!seriesList || seriesList.length === 0) return [];
        // Flatten for Recharts: [{ date: '...', google: 5, gpt4: 1... }]
        const dateMap = {};

        seriesList.forEach(series => {
            series.data.forEach(point => {
                const dateKey = new Date(point.timestamp).toLocaleDateString();
                if (!dateMap[dateKey]) dateMap[dateKey] = { date: dateKey };
                // Invert rank for visualization (Rank 1 is "higher" visually)
                // Or keep separate axis. Let's start with raw rank but inverted Y-axis.
                dateMap[dateKey][series.engine] = point.rank;
            });
        });

        return Object.values(dateMap).sort((a, b) => new Date(a.date) - new Date(b.date));
    };

    const renderLineChart = (chartData) => (
        <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grid} vertical={false} />
                <XAxis dataKey="date" stroke={COLORS.text} fontSize={12} tickLine={false} axisLine={false} />
                <YAxis reversed={true} domain={[1, 10]} stroke={COLORS.text} fontSize={12} tickLine={false} axisLine={false} />
                <Tooltip
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                />
                <Legend />
                {Object.keys(COLORS).filter(k => k !== 'grid' && k !== 'text').map(key => (
                    <Line key={key} type="monotone" dataKey={key} stroke={COLORS[key]} strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} connectNulls />
                ))}
            </LineChart>
        </ResponsiveContainer>
    );

    return (
        <div className="ui-card" style={{ padding: '0', overflow: 'hidden', marginTop: '24px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px' }}>
            {/* --- HEADER & TABS --- */}
            <div style={{ padding: '16px 24px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                    <h3 style={{ margin: 0, fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <TrendingUp size={20} className="text-blue-600" />
                        Truth vs Attack Analytics
                    </h3>
                </div>

                <div style={{ display: 'flex', gap: '4px' }}>
                    <TabButton active={activeTab === 'audit'} onClick={() => setActiveTab('audit')} icon={Shield} label="Truth (Audit)" />
                    <TabButton active={activeTab === 'attack'} onClick={() => setActiveTab('attack')} icon={Swords} label="Battle Results" />
                    <TabButton active={activeTab === 'impact'} onClick={() => setActiveTab('impact')} icon={CheckCircle2} label="Impact Analysis" />
                </div>
            </div>

            {/* --- CONTENT --- */}
            <div style={{ padding: '24px' }}>
                {loading ? (
                    <div style={{ padding: '40px', textAlign: 'center', color: COLORS.text }}>Loading Analytics...</div>
                ) : (
                    <>
                        {activeTab === 'audit' && (
                            <div className="fade-in">
                                <div style={{ marginBottom: '16px', color: COLORS.text }}>
                                    Historical <strong>organic visibility</strong> before AI Agent intervention.
                                </div>
                                {renderLineChart(transformSeriesData(data.audit))}
                            </div>
                        )}

                        {activeTab === 'attack' && (
                            <div className="fade-in">
                                <div style={{ marginBottom: '16px', color: COLORS.text }}>
                                    Rankings achieved <strong>after autonomous agent attacks</strong>.
                                </div>
                                {renderLineChart(transformSeriesData(data.attack))}
                            </div>
                        )}

                        {activeTab === 'impact' && (
                            <div className="fade-in">
                                <div style={{ marginBottom: '20px', color: COLORS.text }}>
                                    Direct comparison of sessions: <strong>Audit Rank</strong> vs <strong>Attack Rank</strong>.
                                </div>

                                {data.impact && data.impact.length > 0 ? (
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
                                        {data.impact.map((item, idx) => (
                                            <div key={idx} style={{
                                                padding: '16px',
                                                borderRadius: '8px',
                                                border: '1px solid #f1f5f9',
                                                background: item.improvement > 0 ? '#f0fdf4' : '#fff'
                                            }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '12px', color: '#64748b' }}>
                                                    <span>{new Date(item.timestamp).toLocaleDateString()}</span>
                                                    <span style={{ fontWeight: 600, textTransform: 'uppercase' }}>{item.engine}</span>
                                                </div>
                                                <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '4px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                                    {item.keyword}
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '12px' }}>
                                                    <div style={{ textAlign: 'center' }}>
                                                        <div style={{ fontSize: '11px', color: '#64748b' }}>WAS</div>
                                                        <div style={{ fontSize: '16px', fontWeight: 700 }}>{formatRankDisplay(item.audit_rank)}</div>
                                                    </div>
                                                    <ArrowRight size={16} color="#cbd5e1" />
                                                    <div style={{ textAlign: 'center' }}>
                                                        <div style={{ fontSize: '11px', color: '#64748b' }}>NOW</div>
                                                        <div style={{ fontSize: '16px', fontWeight: 700, color: item.improvement > 0 ? '#16a34a' : '#ef4444' }}>
                                                            {formatRankDisplay(item.attack_rank)}
                                                        </div>
                                                    </div>
                                                    {item.improvement > 0 && (
                                                        <div style={{ marginLeft: 'auto', padding: '4px 8px', borderRadius: '100px', background: '#dcfce7', color: '#166534', fontSize: '12px', fontWeight: 600 }}>
                                                            +{item.improvement}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                                        No comparison data available yet. Run more battles!
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default TruthAttackDashboard;
